<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      :icon="AddIcon"
      :label="$t('pages.interestZoneList.modal.new.buttonText')"
      @click="openInterestZoneModal = true"
    />
    <export
      url="/interest_areas/csv"
      :filter="filter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="$t('csv.title.interestZones')"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <interest-zone-modal
    v-model:open="openInterestZoneModal"
    v-model:interest-zone="editedInterestZone"
    @saved="refetch"
  />
  <div class="main-area h-full">
    <interest-zone-map
      v-if="records?.length"
      class="ml-12"
      :busy="loading"
      :points="pointsForMap"
    />

    <div class="ml-12">
      <div class="text-body-regular-bold text-table-text-gray uppercase mb-5" data-v-67ba7ed1="">
        {{ $t('pages.interestZoneList.tableHeader') }}
      </div>

      <div class="table">
        <data-table
          v-model:sort-by="sortKey"
          v-model:sort-direction="sortDirection"
          v-model:checked-ids-list="checkedIdsList"
          class="mb-auto"
          :busy="loading"
          :items="records"
          :columns="columns"
          :visible-columns="visibleColumns"
          :actions="actions"
          :choosable="true"
          :row-link="(item) => ({ name: 'interest-zone-details', params: { interestZoneId: item.id } })"
        />
      </div>
    </div>
  </div>
  <pagination
    v-model:page="page"
    v-model:per-page="perPage"
    class="pagination"
    :total="total"
  />
</template>

<script lang="ts" setup>
import {
  computed, reactive, ref, unref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import FilterPane from '@utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@utils/toolbar/panes/AddFields.vue';
import Toolbar from '@utils/toolbar/Toolbar.vue';
import Pagination from '@utils/table/Pagination.vue';
import DataTable, { type Column } from '@utils/table/Table.vue';
import { collOptionsProvider, enumOptionsProvider, useProvider } from '@utils/table/hooks/provider';
import Action from '@utils/toolbar/Action.vue';
import AddIcon from '@images/panes/add.svg';

import type { Filter } from '@utils/filter/Filter.vue';
import { useDelete } from '@hooks/api';
import Tabs from '@components/structure/navigation/Tabs.vue';
import InterestZoneModal from '@pages/loyalty/InterestZoneModal.vue';
import Export from '@utils/toolbar/panes/Export.vue';
import { format } from 'date-fns';
import { useTableFields } from '@hooks/table-configuration';
import type { HydraCollection, InterestZone, Region } from '@/entities/Entities';
import InterestZoneMap from './InterestZoneMap.vue';
import { formatTimeDurationFromSeconds } from '@/utils/helpers';
import { useQueryRef } from '@/hooks/url';

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 100);
const filter = useQueryRef('filter', {});

const openInterestZoneModal = ref(false);
const editedInterestZone = ref();

const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const { t } = useI18n();

const [deleteInterestZone, { loading: deleting }] = useDelete(t('pages.interestZoneList.actions.deleteWarning'));

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter,
  pagination: {
    page,
    perPage,
  },
});

const actions = [
  {
    title: t('pages.interestZoneList.actions.edit'),
    action: (item: InterestZone) => {
      openInterestZoneModal.value = true;
      editedInterestZone.value = { ...item };
    },
  },
  {
    title: t('pages.interestZoneList.actions.delete'),
    action: async (item: InterestZone) => {
      if (await deleteInterestZone(`/interest_areas/${item.id}`)) {
        // eslint-disable-next-line no-use-before-define
        refetch();
      }
    },
  },
];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: false,
    group: t('pages.interestZoneList.fieldGroups.general'),
    class: 'whitespace-nowrap',
    filter: {
      type: 'text',
      group: t('pages.interestZoneList.filterGroups.general'),
      options: {
        label: t('id'),
      },
    },
  },
  {
    key: 'name',
    title: t('interestZone.name'),
    group: t('pages.interestZoneList.fieldGroups.general'),
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.interestZoneList.filterGroups.general'),
      options: {
        label: t('interestZone.name'),
      },
    },
  },
  {
    key: 'region.name',
    title: t('interestZone.region'),
    group: t('pages.interestZoneList.fieldGroups.general'),
    visible: true,
    filter: {
      key: 'region',
      type: 'dropdown',
      group: t('pages.interestZoneList.filterGroups.general'),
      options: {
        options: collOptionsProvider<Region>('/regions', {
          sort: {
            sortKey: 'name',
            sortDirection: 'ASC',
          },
        }, (value) => ({
          label: value.name,
          value: value.id,
        })),
        label: t('interestZone.region'),
      },
    },
  },
  {
    key: 'type',
    csvTranslations: {
      time: t('interestZone.type.time'),
      distance: t('interestZone.type.distance'),
    },
    title: t('interestZone.type.DEFAULT'),
    group: t('pages.interestZoneList.fieldGroups.general'),
    visible: true,
    formatter: (value: string) => t(`interestZone.type.${value}`),
    filter: {
      type: 'dropdown',
      group: t('pages.interestZoneList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/InterestAreaType', (v) => ({
          label: t(`interestZone.type.${v}`),
          value: v,
        })),
        label: t('interestZone.type.DEFAULT'),
      },
    },
  },
  {
    key: 'radius',
    title: t('interestZone.radius'),
    group: t('pages.interestZoneList.fieldGroups.general'),
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.interestZoneList.filterGroups.general'),
      options: {
        label: t('interestZone.radius'),
      },
    },
  },
  {
    key: 'gisLocation.lat',
    title: t('interestZone.gisLocation.latitude'),
    sortable: false,
    visible: true,
    group: t('pages.interestZoneList.filterGroups.general'),
  },
  {
    key: 'gisLocation.long',
    title: t('interestZone.gisLocation.longitude'),
    sortable: false,
    visible: true,
    group: t('pages.interestZoneList.filterGroups.general'),
  },
  {
    key: 'threshold',
    title: t('interestZone.threshold'),
    group: t('pages.interestZoneList.fieldGroups.general'),
    visible: true,
    computed: (item: InterestZone) => (item.type === 'time' ? (item.threshold ? formatTimeDurationFromSeconds(item.threshold) : '') : item.threshold),
    filter: {
      type: 'duration',
      group: t('pages.interestZoneList.filterGroups.general'),
      options: {
        placeholderFrom: 'hh:mm:ss',
        placeholderTo: 'hh:mm:ss',
        label: t('interestZone.threshold'),
      },
    },
  },
  {
    key: 'multiplier',
    title: t('interestZone.multiplier'),
    group: t('pages.interestZoneList.fieldGroups.general'),
    visible: true,
    filter: {
      type: 'range',
      group: t('pages.interestZoneList.filterGroups.general'),
      options: {
        label: t('interestZone.multiplier'),
      },
    },
  },
  {
    key: 'validityStart',
    title: t('interestZone.validityStart'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.interestZoneList.filterGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.interestZoneList.filterGroups.general'),
      options: {
        label: t('interestZone.validityStart'),
      },
    },
  },
  {
    key: 'validityEnd',
    title: t('interestZone.validityEnd'),
    sortable: true,
    visible: true,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.interestZoneList.filterGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.interestZoneList.filterGroups.general'),
      options: {
        label: t('interestZone.validityEnd'),
      },
    },
  },
  {
    key: 'totalLedgerEntryPoints',
    title: t('interestZone.totalLedgerEntryPoints'),
    group: t('pages.interestZoneList.fieldGroups.general'),
    visible: true,
    sortable: true,
    filter: {
      type: 'range',
      group: t('pages.interestZoneList.filterGroups.general'),
      options: {
        label: t('interestZone.totalLedgerEntryPoints'),
      },
    },
  },
  {
    key: 'totalLedgerEntryAmount',
    title: t('interestZone.totalLedgerEntryAmount'),
    group: t('pages.interestZoneList.fieldGroups.general'),
    visible: true,
    sortable: true,
    computed: (item: InterestZone) => (item.type === 'distance' ? item.totalLedgerEntryAmount ?? 0 : formatTimeDurationFromSeconds(item.totalLedgerEntryAmount ?? 0)),
    filter: {
      type: 'range',
      group: t('pages.interestZoneList.filterGroups.general'),
      options: {
        placeholderFrom: 'm / s',
        placeholderTo: 'm / s',
        label: t('interestZone.totalLedgerEntryAmount'),
      },
    },
  },
];

const visibleColumns = useTableFields(
  columns,
);

const {
  data: items,
  loading: loadingTable,
  refetch,
} = useProvider<HydraCollection<InterestZone>>('/interest_areas', providerParams);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter)) {
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    }
    return [{ key: col.key, ...col.filter }];
  }));

const loading = computed(() => loadingTable.value || deleting.value);

const checkedIdsList = ref<string[]>([]);
const pointsForMap = computed(() => checkedIdsList.value.map((id) => records.value?.find((record) => record.id === id)));
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
.medium {
  width: 300px;
  height: auto;
}
</style>

